// ── RenewalLineageSection — RA-FU-7 renewal chain visualization ──────────────
//
// Displays the renewal chain for a risk acceptance entry when lineage.length > 1.
// Shows: Original → Renewal 1 → Renewal 2 ... with status badges.
// Current entry is highlighted. Links to each entry's detail page.
//
// D-RA.9-PE-4: lineage sourced from GET /:id response (server-computed);
// contains root + direct children of root ordered by created_at ASC.

import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RrTypeChip } from '@/components/risk-acceptances/RrTypeChip';
import { RrStatusCell } from '@/components/risk-acceptances/RrStatusCell';
import type { RenewalLineageEntry } from '@/api/types/risk-acceptance.types';

interface RenewalLineageSectionProps {
  currentEntryId: string;
  lineage: RenewalLineageEntry[];
}

export function RenewalLineageSection({ currentEntryId, lineage }: RenewalLineageSectionProps) {
  const navigate = useNavigate();

  // Only show when there is a chain (more than just this entry)
  if (!lineage || lineage.length <= 1) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Renewal Chain</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-2">
          {lineage.map((entry, index) => {
            const isCurrent = entry.id === currentEntryId;
            const label = entry.parent_acceptance_id === null
              ? 'Original'
              : `Renewal ${index}`;

            return (
              <div key={entry.id} className="flex items-center gap-2">
                {index > 0 && (
                  <ArrowRight className="h-4 w-4 shrink-0 text-text-secondary" />
                )}
                <button
                  onClick={() => navigate(`/risk-acceptances/${entry.id}`)}
                  className={`flex flex-col items-start rounded-md border px-3 py-2 text-left transition-colors hover:bg-bg-surface-2 ${
                    isCurrent
                      ? 'border-accent-cyan bg-accent-cyan-dim/10'
                      : 'border-border-subtle bg-bg-surface'
                  }`}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  <span className="text-xs font-medium text-text-secondary">{label}</span>
                  <span className="mt-0.5 max-w-[140px] truncate text-sm font-medium text-text-primary">
                    {entry.title ?? '(no title)'}
                  </span>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <RrTypeChip entry={entry} />
                    <RrStatusCell entry={entry} />
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
